import { z } from 'zod';
import { BRAND_ASSET_KINDS, CLIENT_STATUSES, LABEL_COLORS } from '../domain';
import { emailSchema } from './auth';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));
const optionalEmail = z
  .union([emailSchema, z.literal(''), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
/** Só dígitos, com DDI opcional. "(11) 98888-7777" → "11988887777". */
const phone = z
  .string()
  .trim()
  .max(30)
  .nullable()
  .optional()
  .transform((v) => (v ? v.replace(/[^\d+]/g, '') || null : null));

export const clientStatusSchema = z.enum(CLIENT_STATUSES);
export const labelColorSchema = z.enum(LABEL_COLORS);

export const clientInput = z.object({
  name: z.string().trim().min(1, 'Informe o nome do cliente').max(120),
  legalName: optionalText(200),
  taxId: optionalText(30),
  brandName: optionalText(120),
  color: labelColorSchema,
  email: optionalEmail,
  whatsapp: phone,
  phone,
  website: optionalText(300),
  status: clientStatusSchema,
  notes: optionalText(10_000),
  ownerUserId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  address: z
    .object({
      street: z.string().max(200).optional(),
      city: z.string().max(120).optional(),
      state: z.string().max(60).optional(),
      zip: z.string().max(20).optional(),
      country: z.string().max(60).optional(),
    })
    .nullable()
    .optional()
    .transform((v) => v ?? null),
});
export type ClientInput = z.input<typeof clientInput>;

export const clientListQuery = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.union([clientStatusSchema, z.literal('all')]).optional(),
});

export const personRef = z.object({ id: z.uuid(), name: z.string(), avatarUrl: z.string().nullable() });

export const clientSummaryDto = z.object({
  id: z.uuid(),
  name: z.string(),
  brandName: z.string().nullable(),
  color: labelColorSchema,
  logoUrl: z.string().nullable(),
  status: clientStatusSchema,
  email: z.string().nullable(),
  whatsapp: z.string().nullable(),
  owner: personRef.nullable(),
  updatedAt: z.string(),
});
export type ClientSummaryDto = z.infer<typeof clientSummaryDto>;

export const clientDto = clientSummaryDto.extend({
  legalName: z.string().nullable(),
  taxId: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  address: z.record(z.string(), z.string()).nullable(),
  notes: z.string().nullable(),
  logoFileId: z.uuid().nullable(),
  createdAt: z.string(),
});
export type ClientDto = z.infer<typeof clientDto>;

export const contactInput = z.object({
  name: z.string().trim().min(1, 'Informe o nome').max(120),
  role: optionalText(80),
  email: optionalEmail,
  whatsapp: phone,
  phone,
  isPrimary: z.boolean().default(false),
});
export type ContactInput = z.input<typeof contactInput>;

export const contactDto = z.object({
  id: z.uuid(),
  name: z.string(),
  role: z.string().nullable(),
  email: z.string().nullable(),
  whatsapp: z.string().nullable(),
  phone: z.string().nullable(),
  isPrimary: z.boolean(),
});
export type ContactDto = z.infer<typeof contactDto>;

export const brandAssetKindSchema = z.enum(BRAND_ASSET_KINDS);
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use o formato #RRGGBB');

/** Dados por tipo. Assets de arquivo usam fileId; os demais, data. */
export const brandAssetInput = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('color'),
    name: z.string().trim().min(1).max(80),
    data: z.object({ hex, role: z.string().max(80).optional() }),
  }),
  z.object({
    kind: z.literal('font'),
    name: z.string().trim().min(1).max(120),
    data: z.object({
      family: z.string().trim().min(1).max(120),
      weights: z.string().max(80).optional(),
      usage: z.string().max(200).optional(),
      url: z.url().optional().or(z.literal('')),
    }),
  }),
  z.object({
    kind: z.literal('voice'),
    name: z.string().trim().min(1).max(120),
    data: z.object({ text: z.string().trim().min(1).max(10_000) }),
  }),
  z.object({
    kind: z.literal('link'),
    name: z.string().trim().min(1).max(120),
    data: z.object({ url: z.url('Link inválido') }),
  }),
  z.object({
    kind: z.enum(['logo', 'logo_variation', 'element', 'texture', 'template', 'brandbook']),
    name: z.string().trim().min(1).max(160),
    fileId: z.uuid(),
    data: z.object({ note: z.string().max(300).optional() }).default({}),
  }),
]);
export type BrandAssetInput = z.infer<typeof brandAssetInput>;

export const brandAssetDto = z.object({
  id: z.uuid(),
  kind: brandAssetKindSchema,
  name: z.string(),
  data: z.record(z.string(), z.unknown()),
  file: z
    .object({ id: z.uuid(), url: z.string(), mimeType: z.string(), sizeBytes: z.number(), kind: z.string() })
    .nullable(),
  rank: z.string(),
  createdAt: z.string(),
});
export type BrandAssetDto = z.infer<typeof brandAssetDto>;

export const activityDto = z.object({
  id: z.uuid(),
  actorName: z.string(),
  actorAvatarUrl: z.string().nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.uuid(),
  data: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type ActivityDto = z.infer<typeof activityDto>;

export const clientUserDto = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string(),
  role: z.enum(['client_admin', 'client_member']),
  status: z.enum(['active', 'disabled']),
});
export type ClientUserDto = z.infer<typeof clientUserDto>;

export const inviteClientUserInput = z.object({
  email: emailSchema,
  role: z.enum(['client_admin', 'client_member']),
});
export type InviteClientUserInput = z.infer<typeof inviteClientUserInput>;
